import './retentionGroupsTable.scss'
import { SortHeader, Table } from '@/shared/components'
import { MagnifyingGlassSVGIcon } from '@/shared/assets'
import { DropdownMenuGroups } from '@/features/analytics/invite-links/ui/InviteLinksTable/DropdownMenuGroups'
import {
    useDeleteInviteLinksGroupChannelsMutation,
    useGetGroupsByChannelIdQuery,
} from '@/shared/api'
import { GreyRubleIconInline, Checkbox } from '@/shared/ui'
import { useNavigate } from 'react-router'
import { useGroupsSearch } from '../hooks/useGroupsSearch'
import { useGroupsSortedData } from '../hooks/useGroupsSortedData'
import {
    GroupsFormattedRowType,
    useGroupsFormattedRows,
} from '../hooks/useGroupsFormattedRows'
import { useInviteLinksStore } from '@/features/analytics/model'
import {
    workspacePath,
    useActiveWorkspace,
} from '@/true-fsd/data/advertiser-account'

export const RetentionGroupsTable = ({
    channelId,
    handleCheckboxChange,
    isRowSelected,
}: {
    channelId: number
    handleCheckboxChange: (row: GroupsFormattedRowType) => void
    isRowSelected: (row: GroupsFormattedRowType) => boolean
}) => {
    const { selectedGroups, clearSelectedGroupsByChannel } =
        useInviteLinksStore()

    const inviteLinkDeleteGroupMutation =
        useDeleteInviteLinksGroupChannelsMutation()

    const navigate = useNavigate()
    const { data: groups = [], isLoading } =
        useGetGroupsByChannelIdQuery(channelId)

    const formattedRows = useGroupsFormattedRows(groups)

    const { searchQuery, setSearchQuery, filteredData } =
        useGroupsSearch(formattedRows)

    const { sortedData, sortConfig, handleSort } =
        useGroupsSortedData(filteredData)

    const tableHead = (
        <tr>
            <th style={{ verticalAlign: 'middle', cursor: 'pointer' }}>
                {/* <div className='border-box'></div> */}
                <div className="flex h-full items-center">
                    <DropdownMenuGroups
                        workspaceId={channelId}
                        selectedGroups={selectedGroups}
                        deleteMutation={inviteLinkDeleteGroupMutation}
                        clearSelectedGroupsById={clearSelectedGroupsByChannel}
                    />
                </div>
            </th>
            <th>
                <div className="search-header">
                    <div className="search-input-wrapper">
                        <MagnifyingGlassSVGIcon className="search-icon" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Название"
                            className="search-input"
                        />
                    </div>
                    <SortHeader<GroupsFormattedRowType>
                        title=""
                        field="groupName"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                </div>
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Кол-во ссылок"
                    field="linksCount"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title={
                        <>
                            Бюджет <GreyRubleIconInline />
                        </>
                    }
                    field="budget"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Дата создания"
                    field="creationDate"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Кол-во подписчиков"
                    field="subscribers"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Охват"
                    field="reach"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Вступлений"
                    field="subscriptions"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="CPM"
                    field="cpm"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="CPS"
                    field="cps"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Отписки"
                    field="unsubscriptions"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
            <th>
                <SortHeader<GroupsFormattedRowType>
                    title="Отписки %"
                    field="unsubscriptionsPercentage"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                />
            </th>
        </tr>
    )

    const { activeWorkspace } = useActiveWorkspace()

    const tableBody = (
        <>
            {sortedData.map((row) => (
                <tr
                    key={row.id}
                    onClick={() =>
                        navigate(
                            workspacePath(
                                activeWorkspace,
                                `analytics/links/${row.id}`
                            )
                        )
                    }
                    className="group-name-link"
                >
                    <td>
                        {/* <div className='border-box'></div> */}
                        <div className="flex h-full items-center justify-center">
                            <Checkbox
                                checked={isRowSelected(row)}
                                onChange={() => {
                                    handleCheckboxChange(row)
                                }}
                                className="row-checkbox"
                            />
                        </div>
                    </td>
                    <td>
                        <span>{row.groupName}</span>
                    </td>
                    <td>{row.linksCount}</td>
                    <td>{row.budget}</td>
                    <td>
                        {new Date(row.creationDate).toLocaleDateString('ru-RU')}
                    </td>
                    <td>{row.subscribers}</td>
                    <td>{row.reach}</td>
                    <td>{row.subscriptions}</td>
                    <td>{row.cpm.toFixed(2)}</td>
                    <td>{row.cps.toFixed(2)}</td>
                    <td>{row.unsubscriptions}</td>
                    <td>{row.unsubscriptionsPercentage.toFixed(2)}%</td>
                </tr>
            ))}
        </>
    )

    return (
        <Table
            isLoading={isLoading}
            thead={tableHead}
            tbody={tableBody}
            className="retention-groups-table"
            baseTableClassName="retention-groups-base-table"
        />
    )
}
